import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { appConfig } from '@common-config/app.config';
import { namespace } from '@common-config/constants';
import type { MyBridgeAgent } from '@common/reducers/backend-api/backend-api';
import { ExternalLink, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentWorkspace } from './AgentWorkspace';
import { BuilderChatContext } from './useBuilderChat';
import { agentEditPath, WorkspaceView } from './workspace-view';

/**
 * The agent's page for "open in new tab". Inside WhatsApp Web a relative
 * `/agents/:id` would resolve to web.whatsapp.com, so the extension links to the
 * web app; the web app itself stays on its own origin (the session is there).
 */
export const agentAppUrl = (agentId: string) =>
  window.location.hostname === 'web.whatsapp.com' ? `${appConfig.APP_ENDPOINT}${agentEditPath(agentId)}` : agentEditPath(agentId);

/** The dialog's width: below WORKSPACE_SPLIT_MIN, so a two-pane view renders as tabs. */
export const WORKSPACE_DIALOG_WIDTH = 820;

/**
 * The agent workspace in a dialog — New and Edit agent inside WhatsApp Web
 * (Quick Agents). The same `AgentWorkspace` as the `/agents` pages; the view is
 * dialog state instead of the URL. A build that creates the agent re-points the
 * workspace at it in place, so the conversation stays on screen.
 */
export interface AgentWorkspaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit this agent; absent = build a new one. */
  agent?: MyBridgeAgent | null;
  /** Bind a NEW agent to one chat. */
  chatContext?: BuilderChatContext;
  isRtl?: boolean;
}

export const AgentWorkspaceDialog: React.FC<AgentWorkspaceDialogProps> = ({ open, onOpenChange, agent, chatContext, isRtl }) => {
  const { t } = useTranslation(namespace);
  const [agentId, setAgentId] = useState<string | null>(agent?.id ?? null);
  const [view, setView] = useState<WorkspaceView | null>(null);
  // Each opening is a fresh workspace (a clean chat for the agent it opens on).
  const [session, setSession] = useState(0);
  useEffect(() => {
    if (!open) return;
    setAgentId(agent?.id ?? null);
    setView(null);
    setSession((n) => n + 1);
    // Re-seed on OPEN only: a refetched agent object must not reset the workspace.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const title = agentId ? t('myBridge.form.editTitle') : t('myBridge.workspace.newTitle');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // The padding keeps the header, messages and composer off the dialog's edges;
        // it takes the workspace's own ground so it reads as room, not a frame.
        className="w-[96vw] sm:max-w-[820px] h-[86vh] max-h-[86vh] p-3 sm:p-4 gap-0 overflow-hidden flex flex-col bg-ai-bg"
        // The close button lives in the workspace header, centered on its row.
        showCloseButton={false}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{t('myBridge.form.subtitle')}</DialogDescription>
        {open && (
          <AgentWorkspace
            key={session}
            inDialog
            maxWidth={WORKSPACE_DIALOG_WIDTH}
            agentId={agentId}
            view={view}
            onViewChange={setView}
            onCreated={(id, next) => { setAgentId(id); setView(next); }}
            chatContext={agentId ? undefined : chatContext}
            isRtl={isRtl}
            headerStart={agentId ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={agentAppUrl(agentId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('myBridge.form.openInNewTab')}
                    className="inline-flex items-center text-ai-text-secondary hover:text-ai-text"
                    data-testid="agent-open-in-new-tab"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </TooltipTrigger>
                <TooltipContent>{t('myBridge.form.openInNewTab')}</TooltipContent>
              </Tooltip>
            ) : undefined}
            headerEnd={
              <DialogClose
                aria-label={t('common.close')}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ai-text-secondary transition-colors hover:bg-ai-muted hover:text-ai-text outline-none focus-visible:ring-2 focus-visible:ring-ai-focus-ring"
                data-testid="agent-workspace-close"
              >
                <X className="h-5 w-5" />
              </DialogClose>
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
